import { useCallback, useRef } from "react";

type PickerDocument = {
  id: string;
  name?: string;
  mimeType?: string;
  url?: string;
};

type PickerResponse = {
  action: string;
  docs?: PickerDocument[];
};

declare global {
  interface Window {
    gapi?: {
      load: (api: string, opts: { callback: () => void; onerror?: () => void }) => void;
    };
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (resp: { access_token?: string; error?: string }) => void;
          }) => { requestAccessToken: (opts?: { prompt?: string }) => void };
        };
      };
      picker?: {
        Action: { PICKED: string; CANCEL: string };
        Feature: { NAV_HIDDEN: string; MULTISELECT_ENABLED: string };
        Response: { ACTION: string; DOCUMENTS: string };
        Document: { ID: string; NAME: string; MIME_TYPE: string; URL: string };
        ViewId: { FOLDERS: string; DOCS: string };
        DocsView: new (
          viewId?: string,
        ) => {
          setIncludeFolders: (v: boolean) => any;
          setSelectFolderEnabled: (v: boolean) => any;
          setMimeTypes: (v: string) => any;
          setParent: (v: string) => any;
        };
        PickerBuilder: new () => {
          enableFeature: (f: string) => any;
          setAppId: (appId: string) => any;
          setDeveloperKey: (key: string) => any;
          setOAuthToken: (token: string) => any;
          addView: (view: any) => any;
          setCallback: (cb: (data: any) => void) => any;
          build: () => { setVisible: (v: boolean) => void };
        };
      };
    };
  }
}

const DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.readonly";
const GOOGLE_GSI_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_GAPI_SRC = "https://apis.google.com/js/api.js";

function loadScriptOnce(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) {
      if (existing.getAttribute("data-loaded") === "true") resolve();
      else existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.addEventListener(
      "load",
      () => {
        script.setAttribute("data-loaded", "true");
        resolve();
      },
      { once: true },
    );
    script.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), {
      once: true,
    });
    document.head.appendChild(script);
  });
}

async function ensurePickerApisLoaded(): Promise<void> {
  await loadScriptOnce(GOOGLE_GSI_SRC, "google-gsi");
  await loadScriptOnce(GOOGLE_GAPI_SRC, "google-gapi");

  if (!window.gapi?.load) throw new Error("Google API (gapi) not available");

  await new Promise<void>((resolve, reject) => {
    window.gapi?.load("picker", {
      callback: resolve,
      onerror: () => reject(new Error("Failed to load Google Picker API")),
    });
  });

  if (!window.google?.picker) throw new Error("Google Picker not available");
  if (!window.google?.accounts?.oauth2?.initTokenClient) {
    throw new Error("Google Identity Services not available");
  }
}

function getEnvOrThrow(): { clientId: string; apiKey: string; appId: string } {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
  const appId = import.meta.env.VITE_GOOGLE_APP_ID;

  console.log("[Google Picker env]", {
    VITE_GOOGLE_CLIENT_ID: clientId,
    VITE_GOOGLE_API_KEY: apiKey ? `${apiKey.slice(0, 8)}...` : undefined,
    VITE_GOOGLE_APP_ID: appId,
  });

  if (!clientId) throw new Error("Missing VITE_GOOGLE_CLIENT_ID");
  if (!apiKey) throw new Error("Missing VITE_GOOGLE_API_KEY");
  if (!appId) throw new Error("Missing VITE_GOOGLE_APP_ID");

  return { clientId, apiKey, appId };
}

function logTokenScope(token: string): void {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { scope?: string };
    console.log("[Google Picker] Token scope:", payload.scope ?? "(no scope in payload)");
  } catch {
    console.log("[Google Picker] Token received (could not decode scope)");
  }
}

async function getAccessToken(): Promise<string> {
  await ensurePickerApisLoaded();
  const { clientId } = getEnvOrThrow();
  console.log("[Google Picker] Requesting token with scope:", DRIVE_FILE_SCOPE);

  return new Promise<string>((resolve, reject) => {
    const google = window.google;
    const initTokenClient = google?.accounts?.oauth2?.initTokenClient;
    if (!initTokenClient) {
      reject(new Error("Google token client not available"));
      return;
    }

    const tokenClient = initTokenClient({
      client_id: clientId,
      scope: DRIVE_FILE_SCOPE,
      callback: (resp) => {
        if (resp.error) {
          console.log("[Google Picker] Token error:", resp.error);
          reject(new Error(resp.error));
        } else if (!resp.access_token) {
          reject(new Error("No access token returned"));
        } else {
          console.log("[Google Picker] Token received, length:", resp.access_token.length);
          logTokenScope(resp.access_token);
          resolve(resp.access_token);
        }
      },
    });

    tokenClient.requestAccessToken({ prompt: "" });
  });
}

function openPicker(opts: {
  accessToken: string;
  apiKey: string;
  appId: string;
  view: any;
  viewLabel?: string;
  multiselect?: boolean;
}): Promise<PickerResponse> {
  const picker = window.google?.picker;
  if (!picker) throw new Error("Google Picker not available");
  console.log("[Google Picker] Opening picker", {
    viewLabel: opts.viewLabel,
    multiselect: opts.multiselect,
  });
  return new Promise<PickerResponse>((resolve) => {
    const builder = new picker.PickerBuilder()
      .setDeveloperKey(opts.apiKey)
      .setAppId(opts.appId)
      .setOAuthToken(opts.accessToken)
      .setCallback((data: any) => {
        const action = data[picker.Response.ACTION];
        const docs = data[picker.Response.DOCUMENTS]?.map((d: any) => ({
          id: d[picker.Document.ID],
          name: d[picker.Document.NAME],
          mimeType: d[picker.Document.MIME_TYPE],
          url: d[picker.Document.URL],
        }));
        console.log("[Google Picker] Picker callback", {
          action,
          docsCount: docs?.length ?? 0,
          docIds: docs?.map((d: PickerDocument) => d.id) ?? [],
          rawDocuments: data[picker.Response.DOCUMENTS],
        });
        // Only resolve when user has finished (picked or cancel), not on 'loaded'
        if (action === picker.Action.PICKED || action === picker.Action.CANCEL) {
          resolve({ action, docs: docs ?? [] });
        }
      })
      .addView(opts.view);

    if (opts.multiselect) {
      builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
    }
    const built = builder.build();
    built.setVisible(true);
  });
}

export function useGoogleDrivePicker() {
  const loadingRef = useRef<Promise<void> | null>(null);

  const warmup = useCallback(async () => {
    loadingRef.current ??= ensurePickerApisLoaded();
    await loadingRef.current;
  }, []);

  const pickFolder = useCallback(async (): Promise<{ folderId: string; folderName?: string } | null> => {
    console.log("[Google Picker] pickFolder: starting");
    await warmup();
    const { apiKey, appId } = getEnvOrThrow();
    const accessToken = await getAccessToken();
    const picker = window.google?.picker;
    if (!picker) throw new Error("Google Picker not available");

    const view = new picker.DocsView(picker.ViewId.FOLDERS).setIncludeFolders(true).setSelectFolderEnabled(true);

    const res = await openPicker({ accessToken, apiKey, appId, view, viewLabel: "FOLDERS" });
    console.log("[Google Picker] pickFolder: result", { action: res.action, docsCount: res.docs?.length });
    if (res.action === picker.Action.CANCEL) return null;
    const doc = res.docs?.[0];
    if (!doc?.id) return null;
    console.log("[Google Picker] pickFolder: selected", { folderId: doc.id, folderName: doc.name });
    return { folderId: doc.id, folderName: doc.name };
  }, [warmup]);

  const pickFilesInFolder = useCallback(
    async (parentFolderId: string): Promise<PickerDocument[] | null> => {
      console.log("[Google Picker] pickFilesInFolder: starting", { parentFolderId });
      await warmup();
      const { apiKey, appId } = getEnvOrThrow();
      const accessToken = await getAccessToken();
      const picker = window.google?.picker;
      if (!picker) throw new Error("Google Picker not available");

      const view = new picker.DocsView(picker.ViewId.DOCS).setParent(parentFolderId);
      console.log("[Google Picker] pickFilesInFolder: view ViewId.DOCS, setParent:", parentFolderId);
      const res = await openPicker({
        accessToken,
        apiKey,
        appId,
        view,
        viewLabel: "DOCS (parent=" + parentFolderId + ")",
        multiselect: true,
      });
      console.log("[Google Picker] pickFilesInFolder: result", {
        action: res.action,
        docsCount: res.docs?.length ?? 0,
      });
      if (res.action === picker.Action.CANCEL) return null;
      return res.docs ?? [];
    },
    [warmup],
  );

  const pickFolderThenFiles = useCallback(async (): Promise<{
    folderId: string;
    folderName?: string;
    docs: PickerDocument[] | null;
  } | null> => {
    console.log("[Google Picker] pickFolderThenFiles: step 1 pickFolder");
    const picked = await pickFolder();
    if (!picked) {
      console.log("[Google Picker] pickFolderThenFiles: cancelled at folder step");
      return null;
    }
    console.log("[Google Picker] pickFolderThenFiles: step 2 pickFilesInFolder for", picked.folderId);
    const docs = await pickFilesInFolder(picked.folderId);
    console.log("[Google Picker] pickFolderThenFiles: done", {
      folderId: picked.folderId,
      docsCount: docs?.length ?? 0,
    });
    return { folderId: picked.folderId, folderName: picked.folderName, docs };
  }, [pickFolder, pickFilesInFolder]);

  return { warmup, pickFolder, pickFilesInFolder, pickFolderThenFiles };
}
